import { ProductView } from '@components/product/ProductView'
import { getFullImageUrl } from '@utils/getFullImageUrl';
import { getProductTitle } from '@utils/getProductTitle';
import { parseDescriptionWithBreadCrumbs } from '@utils/parseDescriptionWithBreadCrumbs';
import useSWRImmutable from 'swr/immutable'
import { useRouter } from 'next/dist/client/router'
import { Helmet } from '@components/helmet'
import type { ProductViewProps } from '@components/product/ProductView/ProductView'
import type { Locale } from '@model/locales'
import { KK_LANG_IDS, KK_LOCALE_MAP } from '@model/locales'
import type { Product } from '@model/product'
import type { RecommendationsResult } from '@model/product/ProductSearchResult'
import axios from 'axios'
import { useAppState } from '@context/state'
import type { NextPage } from 'next'
import { useEffect, useMemo } from 'react'
import { useCurrency } from '@hooks/useCurrency'
import { unSlug } from '@utils/slugs'
import { CwsGrid, CwsGridCol, CwsGridRow, CwsSection } from '@components/cws'
import { makeConfig } from '@components/category/HomePageCollection/config'
import type { Catalog } from '@model/pricelist/Catalog'
import { localStorageHelper } from '@utils/localStorageHelper'
import { GUEST_CUSTOMER_HEADER } from '@utils/axios-utils'
import { ProductViewBody } from '@pages/api/register-view'
import { truncateText } from '@utils/truncateText'
import { PERCENTAGE_OF_POINT_USED } from '../../constants/product'

export type KeyValue = {
  [key: string]: string | number | boolean | string[] | undefined
}

export const getFetcher = (apiPath: string, bodyData: KeyValue) => {
  return async () => {
    const { data } = await axios.post(`/api/${apiPath}`, bodyData)
    return data
  }
}

export const ProductPage: NextPage<ProductViewProps> = ({ product, reviews }) => {
  const router = useRouter()

  const guestCustomerId = localStorageHelper.get(GUEST_CUSTOMER_HEADER)
  const { slug, productId, catalogId = 'shop' } = router.query
  const sku = productId && !slug ? product.sku : unSlug(slug as string)
  const { currency } = useCurrency()
  const {
    state: { customer },
  } = useAppState()

  const { taxExemption: coMultiplier } = customer || { taxExemption: '1' }
  const productWithCOPoint = useMemo(() => {
    if (!customer || !coMultiplier) return product

    return {
      ...product,
      [PERCENTAGE_OF_POINT_USED]: `${+product[PERCENTAGE_OF_POINT_USED] * parseFloat(coMultiplier)}`,
    }
  }, [customer, coMultiplier, product])

  useEffect(() => {
    window.product = product
  }, [product])

  const currentCustomerId = customer?.id || guestCustomerId

  const currentLocale = (router.locale as Locale) || 'en'
  const languageId = KK_LANG_IDS[currentLocale]
  const localeMap = KK_LOCALE_MAP[currentLocale as Locale]
  useEffect(() => {
    const data: ProductViewBody = {
      productId: product.id,
      customerId: currentCustomerId,
      languageId,
    }
    axios.post(`/api/register-view`, data).catch(console.warn)
  }, [product, currentCustomerId, languageId])

  makeConfig(languageId, catalogId as Catalog)
  const { data: recommendations } = useSWRImmutable<RecommendationsResult>(
    `/api/recommendations/${router.locale}/${currentCustomerId}/${sku}`,
    getFetcher('recommendations', {
      languageId,
      customerId: currentCustomerId,
      sku,
      catalogId,
    })
  )

  const { data: recentlyViewedProducts } = useSWRImmutable<Product[]>(
    `/api/last-seen-products/${currentCustomerId}/${sku}`,
    getFetcher('last-seen-products', {
      languageId,
      customerId: currentCustomerId,
      catalogId,
    })
  )

  if (!sku) {
    return null
  }

  if (!product) {
    return null
  }

  const description = product.description
    ? parseDescriptionWithBreadCrumbs(product.description).description
    : product.name?.trim()
  const descriptionText = truncateText(description, 155)
  const imageUrlLoader = ({ src, width, quality }: { src: string; width: number; quality?: number }) => {
    return `${src}&w=${width}&q=${quality || 75}`
  }

  const generatedImageUrl = `${window.location.origin}/_next/image?url=${imageUrlLoader({
    src: getFullImageUrl(product.image),
    width: 3840,
    quality: 100,
  })}`

  return (
    <CwsSection className="cws-pt-m">
      <CwsGrid>
        <CwsGridRow>
          <CwsGridCol col="12">
            <Helmet
              type="product"
              imageUrl={product.image ? generatedImageUrl : ''}
              description={descriptionText}
              title={`${product.manufacturerName} - ${getProductTitle(product)}`}
              locale={localeMap}
              sku={product.sku}
              price={product.priceInfo.topPrice.toString()}
              currency={currency}
              brand={product.manufacturerName}
              stock={product.status === 0 ? 'out stock' : 'in stock'}
              url={window.location.href}
              condition={'new'}
            />
            <ProductView
              product={productWithCOPoint}
              reviews={reviews}
              recommendations={recommendations || { productArray: [] }}
              recentlyViewedProducts={recentlyViewedProducts || []}
            />
          </CwsGridCol>
        </CwsGridRow>
      </CwsGrid>
    </CwsSection>
  )
}

export default ProductPage
